import { useCallback, useMemo } from "react";

import type { Product } from "@jti/magento";
import { useSelector } from "@jti/store/reduxHooks";
import { CheckoutV2LoyaltyChoice } from "@jti/store/slices/checkoutV2Slice";

import { useLoyaltyCampaignManagement } from "@checkout-v2/features/LoyaltyPoints/hooks";
import { calculateLoyaltyCartPoints } from "@checkout-v2/features/LoyaltyPoints/utils";
import { useLoyaltyActiveStatus } from "@checkout-v2/shared/hooks/useLoyaltyActiveStatus";
import { checkIfSubscriptionOrder } from "@checkout-v2/shared/utils/checkIfSubscriptionOrder";

import type { LoyaltyPreviewState } from "../../components/PaymentMethodsSectionPreview/PaymentMethodsSectionPreview.types";
import { useLoyaltyPreview } from "../useLoyaltyPreview";

type UseCartPaymentLoyaltyPreviewArgs = {
  cartId?: string;
  cartItems?: Product[];
  onPaymentSectionEdit?: () => void;
};

export const useCartPaymentLoyaltyPreview = ({
  cartId,
  cartItems,
  onPaymentSectionEdit,
}: UseCartPaymentLoyaltyPreviewArgs): LoyaltyPreviewState => {
  const previewState = useLoyaltyPreview(cartId);
  const isLoyaltyActive = useLoyaltyActiveStatus();
  const loyaltyChoice = useSelector((state) => state.checkoutV2.loyaltyPointsState.loyaltyChoice);
  const { userPoints, isRedeemAvailable, setRedeem } = useLoyaltyCampaignManagement();

  const onLoyaltyRedeem = useCallback(() => {
    setRedeem();
    onPaymentSectionEdit?.();
  }, [onPaymentSectionEdit, setRedeem]);

  const collectPreviewState = useMemo<LoyaltyPreviewState>(() => {
    if (previewState.isLoyaltyInfoVisible) {
      return previewState;
    }

    if (!isLoyaltyActive || !cartItems?.length || checkIfSubscriptionOrder(cartItems)) {
      return previewState;
    }

    if (loyaltyChoice !== undefined && loyaltyChoice !== CheckoutV2LoyaltyChoice.COLLECT) {
      return previewState;
    }

    const cartPoints = calculateLoyaltyCartPoints(cartItems);
    if (cartPoints <= 0) {
      return previewState;
    }

    return {
      isLoyaltyInfoVisible: true,
      loyaltyInfo: {
        choice: CheckoutV2LoyaltyChoice.COLLECT,
        points: cartPoints,
      },
    };
  }, [cartItems, isLoyaltyActive, loyaltyChoice, previewState]);

  const paymentLoyaltyPreview = useMemo(
    () => ({
      ...collectPreviewState,
      loyaltyInfo: collectPreviewState.loyaltyInfo
        ? {
            ...collectPreviewState.loyaltyInfo,
            userPoints,
            onRedeem: isRedeemAvailable ? onLoyaltyRedeem : undefined,
          }
        : undefined,
    }),
    [collectPreviewState, isRedeemAvailable, onLoyaltyRedeem, userPoints]
  );

  return paymentLoyaltyPreview;
};
